/**
 * anomalyEngine.js — Statistical Anomaly Detection for Sales & Tickets
 *
 * Algorithms:
 *  1. Z-Score — detects how far a value deviates from historical mean/stddev
 *  2. IQR Fences — Tukey method, robust to outliers in small samples
 *  3. Velocity Detection — rate-of-change spike detection for real-time data
 *
 * Usage:
 *   import { detectRevenueAnomalies, detectTicketAnomalies, computeSalesStats } from './anomalyEngine';
 */

// ─── Z-Score helpers ──────────────────────────────────────────────────────────
function mean(arr) {
  return arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0;
}

function stddev(arr, mu) {
  if (arr.length < 2) return 0;
  const m = mu !== undefined ? mu : mean(arr);
  const variance = arr.reduce((s, v) => s + (v - m) ** 2, 0) / (arr.length - 1);
  return Math.sqrt(variance);
}

function zScore(value, mu, sigma) {
  return sigma > 0 ? (value - mu) / sigma : 0;
}

// IQR fences (Tukey)
function iqrFences(arr) {
  const sorted = [...arr].sort((a, b) => a - b);
  const n = sorted.length;
  const q1 = sorted[Math.floor(n * 0.25)];
  const q3 = sorted[Math.floor(n * 0.75)];
  const iqr = q3 - q1;
  return { q1, q3, lower: q1 - 1.5 * iqr, upper: q3 + 1.5 * iqr };
}

// Severity from z-score magnitude
function zToSeverity(z) {
  const abs = Math.abs(z);
  if (abs >= 3.0) return 'critical';
  if (abs >= 2.0) return 'high';
  if (abs >= 1.5) return 'medium';
  return 'low';
}

// ─── Revenue Anomaly Detection ────────────────────────────────────────────────
/**
 * Detect revenue anomalies for today's running total.
 *
 * @param {number}   todayRevenue     — current running revenue for today
 * @param {number}   elapsedMinutes   — how many minutes into the operating day
 * @param {Object}   salesHistory     — { 'YYYY-MM-DD': revenue } past 60 days
 * @param {number}   todayDOW         — 0=Sun..6=Sat
 * @returns {Array<AnomalyResult>}
 */
export function detectRevenueAnomalies(todayRevenue, elapsedMinutes, salesHistory, todayDOW) {
  const anomalies = [];
  const entries = Object.entries(salesHistory)
    .filter(([, rev]) => rev !== null && rev > 0)
    .map(([d, rev]) => ({ d, rev }));

  if (entries.length < 5) return anomalies; // not enough history

  // ── 1. Day-of-week paced comparison ──
  // Project today's revenue to EOD assuming linear pace
  const DAY_MINUTES = 9 * 60; // assume ~9 hr operating day
  const projectedEOD = elapsedMinutes > 30
    ? todayRevenue * (DAY_MINUTES / Math.max(elapsedMinutes, 60))
    : null;

  const sameDOW = entries.filter(e => {
    const d = parseIsoLocal(e.d);
    return d.getDay() === todayDOW;
  }).map(e => e.rev);

  if (sameDOW.length >= 3 && projectedEOD !== null) {
    const mu    = mean(sameDOW);
    const sigma = stddev(sameDOW, mu);
    const z     = zScore(projectedEOD, mu, sigma);
    const { lower } = iqrFences(sameDOW);

    if (Math.abs(z) >= 1.5) {
      const dir = z > 0 ? 'above' : 'below';
      const pct = Math.abs((projectedEOD - mu) / mu * 100).toFixed(0);
      anomalies.push({
        id:       'revenue_dow_pace',
        type:     'revenue',
        severity: zToSeverity(z),
        title:    `Revenue ${z > 0 ? 'Surge' : 'Dip'} vs Same-Day Baseline`,
        message:  `Today's projected EOD revenue ($${Math.round(projectedEOD).toLocaleString()}) is ${pct}% ${dir} the typical ${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][todayDOW]} average ($${Math.round(mu).toLocaleString()}). Z-score: ${z.toFixed(2)}.`,
        value:    projectedEOD,
        baseline: mu,
        z,
      });
    } else if (projectedEOD < lower && lower > 0) {
      anomalies.push({
        id:       'revenue_iqr_low',
        type:     'revenue',
        severity: 'medium',
        title:    'Revenue Below Historical Range',
        message:  `Projected EOD ($${Math.round(projectedEOD).toLocaleString()}) is below the typical lower bound ($${Math.round(lower).toLocaleString()}) for ${['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][todayDOW]}s.`,
        value:    projectedEOD,
        baseline: mu,
        z,
      });
    }
  }

  // ── 2. Rolling 30-day absolute revenue anomaly ──
  const recent30 = entries.slice(-30).map(e => e.rev);
  if (recent30.length >= 10 && projectedEOD !== null) {
    const mu30    = mean(recent30);
    const sigma30 = stddev(recent30, mu30);
    const z30     = zScore(projectedEOD, mu30, sigma30);
    if (Math.abs(z30) >= 2.5) {
      anomalies.push({
        id:       'revenue_30d_anomaly',
        type:     'revenue',
        severity: zToSeverity(z30),
        title:    `Unusual Revenue vs 30-Day Trend (${z30 > 0 ? '+' : ''}${z30.toFixed(2)}σ)`,
        message:  `Projected $${Math.round(projectedEOD).toLocaleString()} is a ${Math.abs(z30).toFixed(1)}σ ${z30 > 0 ? 'spike above' : 'drop below'} your 30-day average of $${Math.round(mu30).toLocaleString()}. This is statistically unusual.`,
        value:    projectedEOD,
        baseline: mu30,
        z: z30,
      });
    }
  }

  return anomalies;
}

// ─── Average Ticket Size Anomaly ─────────────────────────────────────────────
/**
 * Detects when avg ticket size is unusually high or low.
 *
 * @param {number}   avgTicket        — today's average ticket size
 * @param {Array}    historicalAvgs   — array of historical avg ticket values
 */
export function detectAvgTicketAnomaly(avgTicket, historicalAvgs) {
  const anomalies = [];
  if (!avgTicket || historicalAvgs.length < 5) return anomalies;

  const mu    = mean(historicalAvgs);
  const sigma = stddev(historicalAvgs, mu);
  const z     = zScore(avgTicket, mu, sigma);

  if (Math.abs(z) >= 1.8) {
    const dir = z > 0 ? 'higher' : 'lower';
    anomalies.push({
      id:       'avg_ticket_anomaly',
      type:     'ticket_value',
      severity: zToSeverity(z),
      title:    `Avg Ticket ${z > 0 ? 'Unusually High' : 'Unusually Low'} (${z > 0 ? '+' : ''}${z.toFixed(1)}σ)`,
      message:  `Today's avg ticket ($${avgTicket.toFixed(2)}) is ${Math.abs((avgTicket - mu) / mu * 100).toFixed(0)}% ${dir} than your historical average ($${mu.toFixed(2)}). ${z > 0 ? 'Customers are ordering more — check if a high-price item is trending.' : 'Check if discounts or smaller items are driving the dip.'}`,
      value:    avgTicket,
      baseline: mu,
      z,
    });
  }
  return anomalies;
}

// ─── Velocity / Rush Detection ────────────────────────────────────────────────
/**
 * Detect sudden surges in ticket opening rate.
 * Uses a sliding window comparison: last 10 min vs prior 20 min.
 *
 * @param {Array<{timestamp: number}>} recentEvents  — ticket open timestamps (ms)
 * @param {number}                     now            — current time ms
 * @returns {{ rushDetected, recentCount, priorCount, multiplier }}
 */
export function detectTicketVelocity(recentEvents, now = Date.now()) {
  const TEN_MIN  = 10 * 60 * 1000;
  const THIRTY_MIN = 30 * 60 * 1000;

  const recent = recentEvents.filter(e => now - e.timestamp <= TEN_MIN).length;
  const prior  = recentEvents.filter(e => {
    const age = now - e.timestamp;
    return age > TEN_MIN && age <= THIRTY_MIN;
  }).length;

  // Normalize prior to 10-min window (prior covers 20 min)
  const priorNormalized = prior / 2;
  const multiplier = priorNormalized > 0 ? recent / priorNormalized : 0;

  return {
    rushDetected: recent >= 3 && multiplier >= 2.0,
    recentCount:  recent,
    priorCount:   Math.round(priorNormalized),
    multiplier,
  };
}

// ─── Stale Ticket Detection ───────────────────────────────────────────────────
/**
 * Find tickets that have been open too long.
 * @param {Array<{id, openedAt: number}>} openTickets
 * @param {number} thresholdMinutes  — default 20
 */
export function detectStaleTickets(openTickets, thresholdMinutes = 20) {
  const now = Date.now();
  return openTickets
    .filter(t => (now - t.openedAt) >= thresholdMinutes * 60 * 1000)
    .map(t => ({
      ...t,
      minutesOpen: Math.round((now - t.openedAt) / 60000),
    }))
    .sort((a, b) => b.minutesOpen - a.minutesOpen);
}

// ─── Full Stats Object ────────────────────────────────────────────────────────
/**
 * Compute comprehensive stats object from sales history.
 * Used to pass to the AI insights API.
 *
 * @param {number}   todayRevenue
 * @param {number}   todayTickets
 * @param {Object}   salesHistory  — { 'YYYY-MM-DD': revenue }
 * @param {number}   elapsedMinutes
 */
export function computeSalesStats(todayRevenue, todayTickets, salesHistory, elapsedMinutes) {
  const todayDOW = new Date().getDay();
  const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const entries   = Object.entries(salesHistory)
    .filter(([, r]) => r !== null && r > 0)
    .sort((a, b) => a[0].localeCompare(b[0]));

  // Projected EOD
  const DAY_MINUTES = 9 * 60;
  const expectedEOD = elapsedMinutes >= 30
    ? todayRevenue * (DAY_MINUTES / Math.max(elapsedMinutes, 60))
    : null;

  // Yesterday
  const yesterdayStr = isoDate(addDays(new Date(), -1));
  const yesterday    = salesHistory[yesterdayStr];

  // Last week same day
  const lastWeekStr = isoDate(addDays(new Date(), -7));
  const lastWeek    = salesHistory[lastWeekStr];

  const vsYesterday      = yesterday && todayRevenue   ? (todayRevenue / yesterday - 1)   : null;
  const vsLastWeekSameDay = lastWeek && expectedEOD    ? (expectedEOD / lastWeek - 1)      : null;

  const allRevs   = entries.map(([, r]) => r);
  const todayAvgTicket = todayTickets > 0 ? todayRevenue / todayTickets : 0;

  return {
    todayRevenue,
    todayTickets,
    todayAvgTicket,
    expectedEOD,
    dayName:           DAY_NAMES[todayDOW],
    vsYesterday,
    vsLastWeekSameDay,
    historicalMean:    mean(allRevs),
    historicalStddev:  stddev(allRevs, mean(allRevs)),
    nHistoricalDays:   entries.length,
  };
}

// ─── All Anomalies Combined ───────────────────────────────────────────────────
/**
 * Run all detectors and return unified anomaly list.
 */
export function runAllAnomalyDetectors({
  todayRevenue, todayTickets, elapsedMinutes,
  salesHistory, openTickets, ticketEventLog,
}) {
  const anomalies = [];
  const todayDOW = new Date().getDay();

  // Revenue anomalies
  const revAnomalies = detectRevenueAnomalies(todayRevenue, elapsedMinutes, salesHistory, todayDOW);
  anomalies.push(...revAnomalies);

  // Avg ticket anomaly
  const todayAvgTicket = todayTickets > 0 ? todayRevenue / todayTickets : 0;
  const histEntries = Object.values(salesHistory).filter(r => r > 0);
  // Use revenue/10 as proxy for avg ticket (rough estimate) when actual history unavailable
  const histAvgTickets = histEntries.map(r => r / 10);
  if (todayAvgTicket > 0) {
    anomalies.push(...detectAvgTicketAnomaly(todayAvgTicket, histAvgTickets));
  }

  // Ticket velocity
  const velocity = detectTicketVelocity(ticketEventLog || [], Date.now());

  // Stale tickets
  const stale = detectStaleTickets(openTickets || [], 20);

  return {
    anomalies,
    velocity,
    staleTickets: stale,
    openCount:    (openTickets || []).length,
    staleCount:   stale.length,
    rushDetected: velocity.rushDetected,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function parseIsoLocal(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m-1, d);
}
function addDays(d, n) {
  const r = new Date(d); r.setDate(r.getDate() + n); return r;
}
